
import { Component, Vue } from "vue-property-decorator";
import { mapGetters, mapMutations, mapState } from "vuex";
import {
  CompanyAsNewUser,
  ConsumptionAsReqBody,
  Credentials,
  DocumentsAsReqBody,
  ContractDocumentsAsReqBody,
  NewEmailProps,
  PasswordsAsReqBody,
  ReportAsReqBody,
  SetOfDocumentsAsReqBody,
  DownloadDocumentAsReqBody,
  GetContractDocumentAsReqBody,
  SetDocumentEdoStatusReq,
  Turnovers,
  TurnoversDetails,
  AppealHistoryRefreshReq,
  AppealHistoryFilesRefreshReq,
  AppealFilesReq,
  AppealCustomFieldsReq,
  NotificationFilesReq,
  AsyncJobResultReq,
  AppealTemplateDownloadReq,
} from "@/models/app-api";
import { Contract } from "@/models/contract";
import { AccountingPoint } from "@/models/accounting-point";
import {
  AppealTypes,
  AppealThemes,
} from "@/models/appeals";
import appApi from "@/assets/scripts/app-api";
import { Indication } from "@/models/indications";
import AsyncJob from "@/models/async-job";
import moment from "moment";
import Environment from "@/models/environment";

@Component({
  computed: {
    ...mapState({
      appEnvironment: "appEnvironment",
      appConfig: "appConfig",
      sidebar: "sidebar",
    }),
    ...mapGetters({
      sortedContractsByOrganization: "contracts/sortedContractsByOrganization",
      currentContractId: "contract/id",
      unreadAppealsCount: "appeals/unreadAppealsCount"
    }),
    ...mapGetters("user", {
      passwordChangeRequired: "passwordChangeRequired",
      userId: "id",
    }),
    ...mapGetters("asyncJobs", {
      asyncJobs: "asyncJobs",
    }),
  },
  methods: {
    ...mapMutations("appConfig", {
      setAppConfig: "setAppConfig",
    }),
    ...mapMutations("contracts", {
      setContracts: "setContracts",
      setBalances: "setBalances",
    }),
    ...mapMutations("contract", {
      setContract: "setContract",
    }),
    ...mapMutations("accountingPoints", {
      setAccountingPoints: "setPoints",
      setAccountingHistoryPoints: "setHistoryPoints",
      setAccountingPointsPU: "setPointsPU",
    }),
    ...mapMutations("accountingPoint", {
      setPoint: "setPoint",
      setDevice: "setDevice",
      setDeviceHistory: "setDeviceHistory",
      setPointMeasuringComplex: "setMeasuringComplex",
      setPointParams: "setParams",
      setPointPlanServices: "setPlanServices",
      setPointPlanValues: "setPlanValues",
      setPointHistory: "setPointHistory",
      setPointOtherEquipment: "setOtherEquipment",
    }),
    ...mapMutations("hourlyConsumption", {
      setAskueObjects: "setAskueObjects",
      setPointHourlyPlan: "setPointHourlyPlan",
      setPointHourlyFact: "setPointHourlyFact",
    }),
    ...mapMutations("asd", {
      setAsdObjects: "setAsdObjects",
    }),
    ...mapMutations("organization", {
      setOrganization: "setOrganization",
      setContacts: "setContacts",
      setPaymentAccounts: "setPaymentAccounts",
      setAgents: "setAgents",
    }),
    ...mapMutations("error", {
      setShowError: "setShow",
      setErrorMessage: "setMessage",
    }),
    ...mapMutations({
      setUser: "user/setUser",
      setLoggedIn: "auth/setLoggedIn",
      setappEnvironment: "appEnvironment/setConstants",
      setPreloader: "preloader/setShow",
      setCounterValue: "sidebar/setCounterValue"
    }),
    ...mapMutations("appeals", {
      setAppealsByContract: "setAppealsByContract",
      setTypes: "setTypes",
      setThemes: "setThemes",
      setFilesCategories: "setFilesCategories",
      setCustomFields: "setCustomFields",
      setAppealHistory: "setAppealHistory",
      setUnreadCounterValue: "setUnreadCounterValue",
      setCurrentAppealInfo: "setCurrentAppealInfo",
    }),
    ...mapMutations("notifications", {
      setNotificationsByContract: "setNotificationsByContract",
      setUnreadNotificationsByContract: "setUnreadNotificationsByContract",
    }),
    ...mapMutations("asyncJobs", {
      setAsyncJobs: "setAsyncJobs",
    }),
  },
})
class AppApiMixin extends Vue {
  [x: string]: any;

  mounted() {}

  /**
   * Обновление капчи
   */
  async refreshCaptchaCode(idCaptcha: string) {
    try {
      return await appApi.refreshCaptchaCode(idCaptcha);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Запуск асинхронной задачи
   *
   * @param name - отображаемое имя
   * @param description - описание
   * @param task - запускаемая задача
   * @param params - параметры задачи
   * @param callback - функция, обрабатывающая результат
   */
  async startAsyncJob(name: string, description: string, task: CallableFunction, params: any = null, callback: CallableFunction = null) {
    // переопределяем флаг скачивания, ждем результат в json
    const download = params?.download || false;
    params = {...params, asyncJob: true, download: false};

    let asyncJobs = this.asyncJobs || [];
    // проверка на повторный запуск
    for (const job of asyncJobs) {
      if (job.taskName == task.name && JSON.stringify(job.taskParams || []) === JSON.stringify(params || [])) {
        this.setErrorMessage('Задача запущена повторно');
        this.setShowError(true);
        return;
      }
    }
    task(params).then(response => {
      const { data } = response;
      const asyncJob: AsyncJob = {
        asyncId: data.result.asyncId,
        started: moment().format(),
        name: name,
        description: description,
        status: 0,
        download: download,
        callback: callback,
        taskName: task.name,
        taskParams: params,
      };
      asyncJobs.push(asyncJob);
      this.setAsyncJobs(asyncJobs);
    });
  }

  /**
   * Остановка асинхронной задачи
   *
   * @param asyncId - идентификатор задачи
   */
  async stopAsyncJob(asyncId) {
    try {
      return await appApi.stopAsyncJob(asyncId);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Получение статуса асинхронных задач
   *
   * @param asyncIds - массив идентификаторов задач
   */
  async statusAsyncJobs(asyncIds: string[]) {
    try {
      const { data } = await appApi.statusAsyncJobs(asyncIds);
      // получены данные по асинхронным задачам
      const asyncJobsResult = this.asyncJobs.map(el => {
        const res = data.filter(_ => _.asyncId == el.asyncId);
        if (res.length == 1 && res[0].status != undefined) {
          el.status = res[0].status;
          el.resultId = res[0].resultId;
        }
        return el;
      });
      this.setAsyncJobs(asyncJobsResult);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Скачивание результата асинхронной задачи
   *
   * @param reqBody
   */
  async downloadAsyncJob(reqBody: AsyncJobResultReq) {
    try {
      return await appApi.downloadAsyncJob(reqBody);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Получение результата асинхронной задачи
   * @param reqBody
   */
  async resultAsyncJob(reqBody: AsyncJobResultReq) {
    try {
      return await appApi.resultAsyncJob(reqBody);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async getAppConfig() {
    try {
      const { data } = await appApi.fetchAppConfig();
      this.setAppConfig(data);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async getAppEnvironment() {
    try {
      const { data } = await appApi.fetchEnvironment();
      this.setappEnvironment(data);

      return true;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * ------------------------------- ОБРАЩЕНИЯ ---------------------------------
   */

  /**
   * Получить список обращений
   * @param onFulfilled
   */
  getAppealsByContract(
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi
      .fetchAppealsByContract()
      .then(({ data: fetchedAppeals }) => {
        this.setAppealsByContract(fetchedAppeals);
        onFulfilled(fetchedAppeals);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  async getAppealHistory(
    appealChat_getProps: AppealHistoryRefreshReq,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi
      .fetchAppealHistory(appealChat_getProps)
      .then(({data: fetchedAppealHistory}) => {
        this.setAppealHistory(fetchedAppealHistory);
        onFulfilled(fetchedAppealHistory);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  async getAppealFiles(
    appealFiles_getProps: AppealHistoryFilesRefreshReq,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi
      .fetchAppealFiles(appealFiles_getProps)
      .then(({data: fetchedAppealFiles}) => {
        onFulfilled(fetchedAppealFiles);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  /**
   * Получение файла из обращения
   * @param params
   */
  getAppealFile(params: AppealFilesReq) {
    return appApi
      .getAppealFile(params)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  /**
   * ---------------------------- ОБРАЩЕНИЯ СОЗДАТЬ ----------------------------
   */

  async getAllAppealTypes(): Promise<AppealTypes[]> {
    try {
      const { data } = await appApi.fetchAppealTypes();
      this.setTypes(data);
      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async getAllAppealThemes(): Promise<AppealThemes[]> {
    try {
      const { data } = await appApi.fetchAppealThemes();
      this.setThemes(data);
      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async getCustomFieldsData(appealFields_getData: AppealCustomFieldsReq) {
    this.setPreloader(true);
    appApi.fetchAppealCustomFields(appealFields_getData)
      .then(({data: fetchedCustomFields}) => {
        this.setCustomFields(fetchedCustomFields);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getAppealTemplateById(reqBody: AppealTemplateDownloadReq) {
    return appApi
      .getAppealTemplate(reqBody)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  /**
   * -------------------------------- ДОКУМЕНТЫ --------------------------------
   */

  /**
   *
   * @param documentsProps
   * @param onFulfilled
   */
  getDocumentsByContract(
    documentsProps: DocumentsAsReqBody,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi
      .fetchDocumentsByContract(documentsProps)
      .then(({ data: fetchedDocuments }) => {
        onFulfilled(fetchedDocuments);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getContractDocumentsByContract(
      contractDocumentsProps: ContractDocumentsAsReqBody,
      onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi
      .fetchContractDocumentsByContract(contractDocumentsProps)
      .then(({ data: fetchedContractDocuments }) => {
        onFulfilled(fetchedContractDocuments);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getContractDocumentByContract(requestProps: GetContractDocumentAsReqBody) {
    return appApi
      .getContractDocumentByContract(requestProps)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  /**
   * ----------------------------------- ЭДО -----------------------------------
   */

  /**
   * Получение списка документов ЭДО
   * @param onFulfilled
   */
  getContractDocumentsEdoByContract(onFulfilled: CallableFunction) {
    this.setPreloader(true);
    appApi
      .fetchContractDocumentsEdoByContract()
      .then(({ data: fetchedContractDocuments }) => {
        onFulfilled(fetchedContractDocuments);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  /**
   * Скачать документ ЭДО
   * @param requestProps
   */
  getContractDocumentEdoByContract(requestProps: GetContractDocumentAsReqBody) {
    return appApi
      .getContractDocumentEdoByContract(requestProps)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  /**
   * Изменить статус документа ЭДО
   * @param requestProps
   */
  async setContractEdoDocumentStatus(requestProps: SetDocumentEdoStatusReq) {
    try {
      return await appApi.setContractDocumentEdoStatus(requestProps);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * ---------------------------------------------------------------------------
   */

  /**
   *
   * @param userId
   */
  async getContracts(userId: number): Promise<Contract[]> {
    try {
      const { data } = await appApi.fetchContracts(userId);
      this.setContracts(data);

      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async getContractsBalances(contracts: Contract[]): Promise<boolean> {
    const contractId = contracts
      .map((contract) => contract["$номерЗаписи"])
      .join(",");

    try {
      const { data } = await appApi.fetchContractsBalances(contractId);
      this.setBalances(data);

      return true;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  fetchNotifications() {
    return appApi
      .fetchNotificationsByContract()
      .then(({ data: fetchedNotifications }) => {
        this.setNotificationsByContract(fetchedNotifications);
        this.setUnreadNotificationsByContract(Array.from(fetchedNotifications).filter(item =>
          item['статус'] == 2
        ).length);
      })
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  getNotificationFiles(notificationId: number, onFulfilled: CallableFunction) {
    return appApi
      .getNotificationFilesByContract(notificationId)
      // .then((axiosRes) => axiosRes)
      .then(({ data: fetchedFiles }) => {
        onFulfilled(fetchedFiles);
      })
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  getFileByNotification(reqBody: NotificationFilesReq) {
    return appApi
      .getFileByNotification(reqBody)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  readNotification(
    notificationId: number,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi.readNotificationByContract(notificationId)
      .then((data) => {
        onFulfilled(data);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  public get environment(): Environment | null {
    return this.appEnvironment.constants;
  }

  chooseContract(contract: Contract, isAlone = false) {
    this.setPointPlanServices(null);
    const contractId = contract["$номерЗаписи"];
    const companyId = contract["организация"];

    !isAlone && this.setPreloader(true);

    return Promise.all([
      appApi.chooseContract(contractId),
      appApi.fetchAccountingPoints(contractId),
      appApi.fetchAccountingHistoryPoints(contractId),
      appApi.fetchOrganization(companyId),
      appApi.fetchContractsBalances(contractId.toString()),
    ])
      .then(
        ([
           { data: fetchedContract },
           { data: fetchedPoints },
           { data: fetchedHistoryPoints },
           { data: fetchedCompany },
           { data: fetchedBalance },
         ]) => {
          this.setContract({ ...contract, ...fetchedContract[0] });
          this.setAccountingPoints(fetchedPoints);
          this.setAccountingHistoryPoints(fetchedHistoryPoints);
          this.setOrganization(fetchedCompany[0]);

          this.fetchNotifications();

          if (!isAlone) {
            if (fetchedBalance[0]['сальдо'] > 0) {
              this.$router.push({
                name: "documents",
                params: {contractId: contractId.toString()},
              });
            } else {
              this.$router.push({
                name: "contractCard",
                params: {contractId: contractId.toString()},
              });
            }
          }
        }
      )
      .catch((e) => console.error)
      .finally(() => {
        this.setPreloader(false);
        if (this.environment && this.environment["ЛКОТКЛОБР"] != 1) {
          appApi.fetchAppealsByContract().then(({data: fetchedAppeals}) => {
            if (fetchedAppeals) {
              let sorted_app = [];
              let app_p1 = Array.from(fetchedAppeals).filter(item =>
                item['непрочитано'] > 0 && item['обращение-результат>признаки'] % 4 >= 2
              );

              let app_p2 = Array.from(fetchedAppeals).filter(item =>
                item['обращение-результат>название'] == ''
              );
              let app_p3 = Array.from(fetchedAppeals).filter(item =>
                item['обращение-результат>название'] != ''
              );

              let set = new Set();

              app_p1.forEach((item) => {
                set.add(item);
              });
              app_p2.forEach((item) => {
                set.add(item);
              });
              app_p3.forEach((item) => {
                set.add(item);
              });
              set.forEach(item => sorted_app.push(item));

              this.setUnreadCounterValue(app_p1);
              this.setAppealsByContract(sorted_app);
            }
          }).catch((e) => {
            console.log(e);
            this.setErrorMessage('Центр обращений не работает. Раздел обращений будет пуст');
          })
        }
      });

  }

  removeContract(contractId: number, onFulfilled: CallableFunction) {
    return appApi
      .removeContract(contractId)
      .then((_) => {
        onFulfilled();

        if (this.currentContractId === contractId) {
          this.setContract(null);
        }

        this.enterApp();
      })
      .catch((e) => Promise.reject(e.message));
  }

  async addContract(newContract: CompanyAsNewUser) {
    try {
      return await appApi.addContract(newContract);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  chooseAccountingPoint(point: AccountingPoint) {
    const pointId = point["лицевой"];
    this.setPreloader(true);

    return Promise.all([
      appApi.fetchPointMeasuringComplex(pointId),
      appApi.fetchPointParams(pointId),
      appApi.fetchPointPlanServices(pointId),
      appApi.fetchPointPlanValues(pointId),
      appApi.fetchPointOtherEquipment(pointId),
    ])
      .then(
        ([
           { data: fetchedMeasuringComplex },
           { data: fetchedParams },
           { data: fetchedServices },
           { data: fetchedPlanValues },
           { data: fetchedOtherEquipment },
         ]) => {
          this.setPoint(point);
          this.setPointMeasuringComplex(fetchedMeasuringComplex);
          this.setPointParams(fetchedParams);
          this.setPointPlanServices(fetchedServices);
          this.setPointPlanValues(fetchedPlanValues);
          this.setPointOtherEquipment(fetchedOtherEquipment);
        }
      )
      .catch(console.error)
      .finally(() => this.setPreloader(false));
  }

  sendPlanValues(
    meteringPointId: number,
    service: number,
    year: number,
    values: { [x: string]: number }
  ) {
    return appApi
      .sendPlanValues(meteringPointId, service, year, values)
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  chooseHourlyPoint(reqBody: { id: number; level: 0 | 1; month: string }) {
    return Promise.all([
      appApi.fetchHourlyPoint({ ...reqBody, mode: 0 }),
      appApi.fetchHourlyPoint({ ...reqBody, mode: 1 }),
    ])
      .then(([{ data: fetchedPlan }, { data: fetchedFact }]) => {
        this.setPointHourlyPlan(fetchedPlan);
        this.setPointHourlyFact(fetchedFact);
      })
      .catch(console.error);
  }

  getAskueObjects(contractId: number) {
    this.setPreloader(true);
    appApi
      .fetchAskueObjects(contractId)
      .then(({ data: objects }) => this.setAskueObjects(objects))
      .catch(console.error)
      .finally(() => this.setPreloader(false));
  }

  getContractPoints(contractId: number) {
    this.setPreloader(true);
    appApi
      .fetchContractPoints(contractId)
      .then(({ data: objects }) => this.setAsdObjects(objects))
      .catch(console.error)
      .finally(() => this.setPreloader(false));
  }

  downloadConsumptionDocument(mode: 0 | 1 | 2, month: string) {
    return appApi.downloadConsumptionDocument(mode, month);
  }

  uploadConsumptionDocument(reqBody: FormData) {
    return appApi.uploadConsumptionDocument(reqBody);
  }

  async fetchPlanValues(meteringPointId: number) {
    try {
      const { data } = await appApi.fetchPointPlanValues(meteringPointId);
      this.setPointPlanValues(data);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  choosePointForHistory(
    point: AccountingPoint,
    onFulfilled: CallableFunction,
    dates: string[]
  ) {
    this.setPreloader(true);

    const pointId = point["лицевой"];
    let isoDates: string[] = [];
    if (!dates) {
      const startDate = new Date();
      const endDate = new Date();

      startDate.setMonth(startDate.getMonth() - 5);
      isoDates = [startDate, endDate].map((date) => date.toISOString());
    }

    const from = dates ? dates[0] : isoDates[0];
    const to = dates ? dates[1] : isoDates[1];

    appApi
      .fetchPointHistory(pointId, from, to)
      .then((fetchedHistory) => {
        const history = fetchedHistory.data.data;

        this.setPoint(point);
        this.setPointHistory(history);

        onFulfilled();
      })
      .catch((reason) => console.error(appApi.onReject(reason)))
      .finally(() => this.setPreloader(false));
  }

  chooseDeviceForHistory(
    device: AccountingPoint,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);

    appApi
      .fetchPUHistory(device['лицевой'], this.currentContractId)
      .then((fetchedHistory) => {
        const history = fetchedHistory.data.data;
        this.setDevice(device);
        this.setDeviceHistory((history || []).filter(_ => _["объект-показания"] == device["счетчик"]));

        onFulfilled();
      })
      .catch((reason) => console.error(appApi.onReject(reason)))
      .finally(() => this.setPreloader(false));
  }

  getOrgAgents(
    organizationId: number,
    onFulfilled: CallableFunction
  ) {
    appApi.fetchOrganizationAgents(organizationId)
      .then(({ data: fetchedAgents }) => {
        this.setAgents(fetchedAgents);
        onFulfilled(fetchedAgents);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  resetUnreadAppeal(
    appealId: number,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    appApi.sendUnreadReset(appealId)
      .then((data) => {
        onFulfilled(data);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getAdditionalOrganizationData(organizationId: number) {
    this.setPreloader(true);

    Promise.all([
      appApi.fetchOrganizationContacts(organizationId),
      appApi.fetchOrganizationPaymentAccounts(organizationId),
      appApi.fetchOrganizationAgents(organizationId),
    ])
      .then(
        ([
           { data: fetchedContacts },
           { data: fetchedPaymentAccounts },
           { data: fetchedAgents },
         ]) => {
          this.setContacts(fetchedContacts);
          this.setPaymentAccounts(fetchedPaymentAccounts);
          this.setAgents(fetchedAgents);
        }
      )
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getOrganizationTurnovers(
    turnoversProps: Turnovers,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);

    appApi
      .fetchOrganizationTurnovers(turnoversProps)
      .then(({ data }) => {
        onFulfilled(data);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getOrganizationTurnoversDetails(
    arrayOfturnoversProps: TurnoversDetails[],
    onFulfilled: CallableFunction
  ) {
    return Promise.all(
      arrayOfturnoversProps.map((props) =>
        appApi.fetchOrganizationTurnoversDetails(props)
      )
    )
      .then((arrayOffetchedTurnoversDetails) => {
        onFulfilled(arrayOffetchedTurnoversDetails.map(({ data }) => data));
      })
      .catch(console.error);
  }

  getOrganizationReports(contractId: number, onFulfilled: CallableFunction) {
    this.setPreloader(true);

    appApi
      .fetchOrganizationReports(contractId)
      .then(({ data: fetchedReports }) => {
        onFulfilled(fetchedReports);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  async getReportByContract(reportProps: ReportAsReqBody) {
    try {
      return await appApi.getReportByContract(reportProps);
    } catch (e) {
      return Promise.reject(appApi.onReject(e));
    }
  }

  getServicesByContract(contractId: number, onFulfilled: CallableFunction) {
    this.setPreloader(true);

    appApi
      .fetchServicesByContract(contractId)
      .then(({ data: fetchedServices }) => {
        onFulfilled(fetchedServices);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getChannelsList(
    consumptionProps,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    return appApi
      .fetchChannelsList(consumptionProps)
      .then(({ data: fetchedChannelsList }) => {
        onFulfilled(fetchedChannelsList);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getTableAnalysis(
    consumptionProps,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    return appApi
      .fetchTableAnalysis(consumptionProps)
      .then(({ data: fetchedTableAnalysis }) => {
        onFulfilled(fetchedTableAnalysis);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getChannels(
    consumptionProps,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    return appApi
      .fetchChannels(consumptionProps)
      .then(({ data: fetchedChannels }) => {
        onFulfilled(fetchedChannels);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getEventsList(
    payload,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);
    return appApi
      .fetchEventsList(payload)
      .then(({ data: fetchedChannelsList }) => {
        onFulfilled(fetchedChannelsList);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getContractPointsWithPromise(contractId: number, onFulfilled:CallableFunction) {
    this.setPreloader(true);
    appApi
      .fetchContractPoints(contractId)
      .then(({ data: fetchedPoints }) => {
        onFulfilled(fetchedPoints);
      })
      .catch(console.error)
      .finally(() => this.setPreloader(false));
  }

  getConsumptionList(
    consumptionProps: ConsumptionAsReqBody,
    onFulfilled: CallableFunction
  ) {
    this.setPreloader(true);

    return appApi
      .fetchConsumptionList(consumptionProps)
      .then(({ data: fetchedConsumptionList }) => {
        onFulfilled(fetchedConsumptionList);
      })
      .catch(console.error)
      .finally(() => {
        this.setPreloader(false);
      });
  }

  getSetOfDocumentsByContract(documentProps: SetOfDocumentsAsReqBody) {
    return appApi
      .fetchSetOfDocumentsByContract(documentProps)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  downloadDocumentByContract(reqBody: DownloadDocumentAsReqBody) {
    return appApi
      .downloadDocumentByContract(reqBody)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  getAnalysts(onFulfilled: CallableFunction) {
    appApi
      .fetchAnalysts()
      .then((fetchedAnalysts) => {
        const analysts = fetchedAnalysts.data.data;
        onFulfilled(analysts);
      })
      .catch((reason) => console.error(appApi.onReject(reason)));
  }

  fetchAccountingPointsPU(contractId: number) {
    appApi
      .fetchAccountingPointsPU(contractId)
      .then((fetchedPoints) => {
        const currentPoints = fetchedPoints.data.data;
        this.setAccountingPointsPU(currentPoints);
      })
      .catch((reason) => console.error(appApi.onReject(reason)))
      .finally(() => {});

  }

  sendCountersNewValue(
    meteringPointId: number,
    counterId: number,
    indications: Array<Indication>,
  ) {
    return appApi
      .sendCountersNewValue(meteringPointId, counterId, indications)
      .then((res) => {
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  sendAppeal(data: FormData) {
    return appApi
      .sendAppeal(data)
      .then((res) => {
        return res.data;
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  sendAppealMessage(data: FormData) {
    return appApi
      .sendAppealMessage(data)
      .then((res) => {
        return res.data;
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  getMassValuesInputTemplateByContract(data: {
    contractId: number;
    download: boolean;
  }) {
    return appApi
      .fetchMassValuesInputTemplateByContract(data)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  getMassValuesInputResultFile(data: {
    fileName: string;
    download: boolean;
  }) {
    return appApi
      .fetchMassValuesInputResultFile(data)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  checkMassValuesInputTemplateByContract(data: FormData) {
    return appApi
      .checkMassValuesInputTemplateByContract(data)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  sendMassValuesInputTemplateByContract(data: FormData) {
    return appApi
      .sendMassValuesInputTemplateByContract(data)
      .then((axiosRes) => axiosRes)
      .catch((e) => Promise.reject(appApi.onReject(e)));
  }

  signIn(credentials: Credentials) {
    return appApi.signIn(credentials).then((user) => {
      const { "x-api-token": xApiToken } = user;

      localStorage.setItem("xApiToken", xApiToken);
      this.setLoggedIn(true);
      this.setUser(user);

      this.$router.push({
        name: this.passwordChangeRequired ? "accountSettings" : "myContracts",
      });
    });
  }

  enterApp(isFirstRun = false) {
    this.setPreloader(true);

    Promise.all([
      this.getContracts(this.userId),
      isFirstRun ? this.getAppEnvironment() : Promise.resolve(true),
    ])
      .then(([contracts, _]) => {
        if (!!contracts.length) {
          return this.getContractsBalances(contracts)
            .then(() => contracts)
            .catch((e) => Promise.reject(e))
        } else {
          return contracts
        }
      })
      .then((contracts) => {
        if (contracts.length === 1) {
          return this.chooseContract(contracts[0], true).catch((e) =>
            Promise.reject(e)
          );
        }
      })
      .catch((_) => {
        this.setLoggedIn(false);
      })
      .finally(() => {
        this.setPreloader(false);
      });
  }

  async signUp(newUser: FormData) {
    try {
      return await appApi.signUp(newUser);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  signOut() {
    appApi
      .signOut()
      .then(() => {
        type Mutation = { name: string; value?: any };

        const mutations: Mutation[] = [
          // { name: "setAppConfig" }, // не перетираем конфиг при выходе
          { name: "setLoggedIn", value: false },
          { name: "setUser" },
          { name: "setContracts" },
          { name: "setBalances" },
          { name: "setContract" },
          { name: "setAccountingPoints" },
          { name: "setAccountingPointsPU" },
          { name: "setPoint" },
          { name: "setPointHistory" },
          { name: "setPointHourlyFact" },
          { name: "setAskueObjects" },
          { name: "setAsdObjects" },
          { name: "setPointHourlyPlan" },
          { name: "setPointMeasuringComplex" },
          { name: "setPointParams" },
          { name: "setOrganization" },
          { name: "setContacts" },
          { name: "setPaymentAccounts" },
          { name: "setAgents" },
          { name: "setPreloader", value: false },
          { name: "setShowError", value: false },
          { name: "setErrorMessage" },
          { name: "setTypes", value: false },
          { name: "setThemes", value: false },
          { name: "setFilesCategories", value: false },
          { name: "setAppealsByContract", value: false },
          { name: "setAppealHistory", value: false },
          { name: "setCurrentAppealInfo", value: false },
          { name: "setNotificationsByContract" },
          { name: "setUnreadNotificationsByContract" },
          { name: "setAsyncJobs" },
        ];

        mutations.forEach((mutation) => {
          const { name, value = null } = mutation;
          this[name](value);
        });
      })
      .catch(console.error)
      .finally(() => {
        const localStorageKeys = ["xApiToken", "personalCompanyAccount"];
        localStorageKeys.forEach((key) => localStorage.removeItem(key));
      });
  }

  async changePassword(passwordsProps: PasswordsAsReqBody): Promise<string[]> {
    try {
      const { message } = await appApi.changePassword(passwordsProps);
      return message;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async changeEmail(email: string) {
    try {
      const { code, data } = await appApi.changeEmail(email);
      return { code , data };
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async confirmEmailChange(newEmailProps: NewEmailProps): Promise<string> {
    try {
      const { message } = await appApi.confirmEmailChange(newEmailProps);
      return message;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async recoveryPassword(login: string): Promise<string> {
    try {
      const { message } = await appApi.recoveryPassword(login);
      return message;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async recoveryPasswordConfirm(login: string, guid: string): Promise<string> {
    try {
      const { message } = await appApi.recoveryPasswordConfirm(login, guid);
      return message;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  getPaymentURL(data: FormData) {
    return appApi
      .getPaymentURL(data)
      .then((res) => {
        return res.data;
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  getPaymentNoAuthURL(data: FormData) {
    return appApi
      .getPaymentNoAuthURL(data)
      .then((res) => {
        return res.data;
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  getPaymentFee() {
    return appApi
      .getPaymentFee()
      .then((res) => {
        return res.data;
      })
      .catch((reason) => {
        return Promise.reject(appApi.onReject(reason));
      });
  }

  downloadPatternRegistrationDocument() {
    return appApi.downloadPatternRegistrationDocument();
  }
}

export default AppApiMixin;
